import { PANEL_GAP, VIEWPORT_PADDING } from '../helpAgentPanel.constants';
const REPOSITION_DELAY_MS = 260;

export interface HelpAgentPanelControllerOptions {
  getPanel: () => HTMLElement | null
  getButton: () => HTMLButtonElement | null
  onOpen: () => void
  onClose: () => void
  focusPanelContent: () => void
}

export class HelpAgentPanelController {
  private isOpen = false;
  private positionFrame: number | null = null;
  private focusFrame: number | null = null;
  private repositionTimer: number | null = null;

  public constructor (private readonly options: HelpAgentPanelControllerOptions) {}

  public connect (): void {
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    window.addEventListener('resize', this.handleResize);
  }

  public disconnect (): void {
    this.cancelPendingWork();
    this.isOpen = false;
    this.resetPanelState();
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    window.removeEventListener('resize', this.handleResize);
  }

  public open (options: { restoreFocus?: boolean } = {}): void {
    this.setOpen(true, options);
  }

  public close (options: { restoreFocus?: boolean } = {}): void {
    this.setOpen(false, options);
  }

  private setOpen (isOpen: boolean, options: { restoreFocus?: boolean }): void {
    const panel = this.options.getPanel();
    const button = this.options.getButton();
    this.isOpen = isOpen;
    panel?.classList.toggle('open', isOpen);
    panel?.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    button?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (isOpen) {
      this.options.onOpen();
      this.schedulePosition();
      if (this.focusFrame !== null) {
        window.cancelAnimationFrame(this.focusFrame);
      }
      this.focusFrame = window.requestAnimationFrame(() => {
        this.focusFrame = null;
        this.options.focusPanelContent();
      });
      return;
    }

    this.cancelPendingWork();
    this.options.onClose();
    if (panel) {
      this.resetPanelState();
    }
    if (options.restoreFocus !== false) {
      button?.focus({ preventScroll: true });
    }
  }

  private resetPanelState (): void {
    const panel = this.options.getPanel();
    const button = this.options.getButton();
    panel?.classList.remove('open');
    panel?.setAttribute('aria-hidden', 'true');
    button?.setAttribute('aria-expanded', 'false');
    if (panel) {
      panel.style.position = '';
      panel.style.right = '';
      panel.style.top = '';
    }
  }

  private schedulePosition (): void {
    if (this.positionFrame !== null) {
      window.cancelAnimationFrame(this.positionFrame);
    }
    if (this.repositionTimer !== null) {
      window.clearTimeout(this.repositionTimer);
    }
    this.positionFrame = window.requestAnimationFrame(() => {
      this.positionFrame = null;
      this.positionPanel();
    });
    this.repositionTimer = window.setTimeout(() => {
      this.repositionTimer = null;
      this.positionPanel();
    }, REPOSITION_DELAY_MS);
  }

  private positionPanel (): void {
    const panel = this.options.getPanel();
    const button = this.options.getButton();
    if (!this.isOpen || panel === null || button === null || !panel.classList.contains('open')) {
      return;
    }

    const buttonRect = button.getBoundingClientRect();
    const right = Math.max(VIEWPORT_PADDING, window.innerWidth - buttonRect.right);
    const requestedTop = buttonRect.bottom + PANEL_GAP;
    const top = requestedTop + panel.offsetHeight > window.innerHeight - VIEWPORT_PADDING
      ? Math.max(VIEWPORT_PADDING, window.innerHeight - panel.offsetHeight - VIEWPORT_PADDING)
      : requestedTop;

    panel.style.position = 'fixed';
    panel.style.right = `${right}px`;
    panel.style.top = `${top}px`;
  }

  private cancelPendingWork (): void {
    if (this.positionFrame !== null) {
      window.cancelAnimationFrame(this.positionFrame);
      this.positionFrame = null;
    }
    if (this.focusFrame !== null) {
      window.cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
    }
    if (this.repositionTimer !== null) {
      window.clearTimeout(this.repositionTimer);
      this.repositionTimer = null;
    }
  }

  private readonly handleResize = (): void => {
    if (this.isOpen) {
      this.schedulePosition();
    }
  };

  private readonly handleDocumentClick = (event: Event): void => {
    if (!this.isOpen) {
      return;
    }
    const panel = this.options.getPanel();
    const button = this.options.getButton();
    const path = event.composedPath();
    if ((panel === null || !path.includes(panel)) && (button === null || !path.includes(button))) {
      this.close({ restoreFocus: false });
    }
  };

  private readonly handleDocumentKeydown = (event: KeyboardEvent): void => {
    if (this.isOpen && event.key === 'Escape') {
      event.stopPropagation();
      this.close({ restoreFocus: true });
    }
  };
}
